const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function request(endpoint, options = {}) {
  const url = `${BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }
      throw new ApiError(
        errorData.error || errorData.message || `HTTP ${response.status}`,
        response.status,
        errorData
      );
    }

    if (response.status === 204) return null;
    return await response.json();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err.name === 'AbortError') throw err;
    throw new ApiError(err.message || 'Network error', 0, null);
  }
}

function buildQuery(params) {
  if (!params) return '';
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const str = query.toString();
  return str ? `?${str}` : '';
}

// Transactions
export function getTransactions(params) {
  return request(`/transactions${buildQuery(params)}`);
}

export function getTransactionSummary() {
  return request('/transactions/summary');
}

// Stock
export function getStock(params) {
  return request(`/stock${buildQuery(params)}`);
}

export function getStockByZone(zone) {
  return request(`/stock/${encodeURIComponent(zone)}`);
}

export function getStockByZoneCategory(zone, category) {
  return request(`/stock/${encodeURIComponent(zone)}/${encodeURIComponent(category)}`);
}

export function getLowStock() {
  return request('/stock/low');
}

export function getStockHealth() {
  return request('/stock/health');
}

// Analytics
export function getPopularItems(params) {
  return request(`/analytics/popular${buildQuery(params)}`);
}

export function getZoneAnalytics() {
  return request('/analytics/zones');
}

export function getItemHistory(classname, params) {
  return request(`/analytics/items/${encodeURIComponent(classname)}/history${buildQuery(params)}`);
}

export function getTrends() {
  return request('/analytics/trends');
}

export function getOverview() {
  return request('/analytics/overview');
}

export function getCategoryStats() {
  return request('/analytics/categories');
}

// Zones
export function getZones() {
  return request('/zones');
}

export function getZoneDetail(name) {
  return request(`/zones/${encodeURIComponent(name)}`);
}

export function getZoneTransfers(name) {
  return request(`/zones/${encodeURIComponent(name)}/transfers`);
}
